// Growem — Plan screen: prioritized muscle map (front/back figures), a tappable
// muscle info panel, the 6-day split grid, and the goals footer line.

import { useState } from 'react'
import { useUI } from '../state/ui'
import { planForDate, musclesActive, GOALS } from '../data/plan'
import { MUSCLE_SHAPES, BODY_OUTLINE, MIRROR_TRANSFORM, type MuscleView } from '../data/muscleShapes'
import { todayKey, dow, planIndexForDow } from '../domain/dates'

// Group innerHTML for a muscle fragment: base + optional mirrored copy.
function shapeHTML(d: string, mirror?: boolean): string {
  return mirror ? d + '<g transform="' + MIRROR_TRANSFORM + '">' + d + '</g>' : d
}

interface FigureProps {
  view: MuscleView
  selected: string | null
  onPick: (slug: string) => void
}

function Figure({ view, selected, onPick }: FigureProps) {
  const outline = BODY_OUTLINE[view].join('')
  const slugs = Object.keys(MUSCLE_SHAPES).filter((s) => MUSCLE_SHAPES[s].v === view)
  return (
    <svg viewBox="0 0 120 205" style={{ width: '100%', maxWidth: 132, display: 'block' }}>
      <defs>
        <filter id="mglow" x="-50%" y="-50%" width="200%" height="200%">
          <feDropShadow dx="0" dy="0" stdDeviation="2" floodColor="#C8F031" floodOpacity="0.7" />
        </filter>
      </defs>
      <g
        fill="none"
        stroke="rgba(244,244,241,0.20)"
        strokeWidth="1.2"
        strokeLinecap="round"
        dangerouslySetInnerHTML={{ __html: outline }}
      />
      {slugs.map((slug) => {
        const sh = MUSCLE_SHAPES[slug]
        const on = selected === slug
        return (
          <g
            key={slug}
            onClick={() => onPick(slug)}
            style={{ cursor: 'pointer' }}
            fill={on ? 'var(--volt-fill-30)' : 'rgba(244,244,241,0.06)'}
            stroke={on ? 'var(--volt)' : 'var(--line-30)'}
            strokeWidth={on ? 1.3 : 0.8}
            filter={on ? 'url(#mglow)' : undefined}
            dangerouslySetInnerHTML={{ __html: shapeHTML(sh.d, sh.mirror) }}
          />
        )
      })}
    </svg>
  )
}

export default function Plan() {
  const { setTab } = useUI()
  const [selected, setSelected] = useState<string | null>(null)
  const pick = (slug: string) => setSelected((cur) => (cur === slug ? null : slug))

  // A PROPOSITO por dia de la semana, no por la rutina efectiva del dia (decision D4): esta grilla es
  // el CALENDARIO semanal del plan, y mover la rutina del martes a un miercoles no convierte el
  // miercoles en martes. Consecuencia aceptada: en un dia con la rutina movida, aqui sigue resaltado
  // el dia que toca por calendario mientras la pantalla de sesion muestra la rutina que de verdad
  // haces. Es intencional; no cablear planIdxOf aqui "por simetria".
  const todayIdx = planIndexForDow(dow(todayKey()))
  const info = selected ? musclesActive()[selected] : null

  const g = GOALS
  const km = (n: number) => n.toLocaleString('en-US')
  const goalsLine =
    `${g.proteinMin}–${g.proteinMax} g · ` +
    `${km(g.kcalMin)}–${km(g.kcalMax)} kcal · ` +
    `${g.waterMinMl / 1000}–${g.waterMaxMl / 1000} L · ` +
    `${g.sleepMinH}–${g.sleepMaxH} h`

  return (
    <div>
      <div className="h1">Plan priorizado</div>

      {/* Muscle map — front + back figures */}
      <div className="group" style={{ marginTop: 14, display: 'flex', justifyContent: 'center', gap: 18 }}>
        <Figure view="f" selected={selected} onPick={pick} />
        <Figure view="b" selected={selected} onPick={pick} />
      </div>

      {/* Selected-muscle info panel */}
      {info && (
        <div className="card mt">
          <div className="row" style={{ justifyContent: 'space-between', alignItems: 'baseline', gap: 8 }}>
            <div className="disp" style={{ fontWeight: 700, fontSize: 18, letterSpacing: '-0.01em' }}>{info.name}</div>
            <div className="note">{info.freq}</div>
          </div>
          <div className="row" style={{ gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
            {info.days.map((d) => (
              <span key={d} className="daybadge">{d}</span>
            ))}
          </div>
          <div className="tip" style={{ marginTop: 8 }}>{info.note}</div>
          <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 6 }}>
            {info.ex.map(([name, mark], i) => (
              <div
                key={i}
                className="row"
                style={{
                  justifyContent: 'space-between', gap: 10,
                  paddingTop: i ? 6 : 0,
                  borderTop: i ? '1px solid var(--line-06)' : undefined,
                }}
              >
                <span style={{ fontSize: 13, color: 'var(--ink-1)' }}>{name}</span>
                <span className="note" style={{ whiteSpace: 'nowrap' }}>{mark}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Days grid — 2×3 split */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 10 }}>
        {planForDate(todayKey()).map((p, i) => {
          const isToday = i === todayIdx
          return (
            <button
              key={p.d}
              onClick={() => setTab('sesion')}
              style={{
                textAlign: 'left',
                background: isToday ? 'var(--surface-2)' : 'var(--surface-1)',
                border: `1px solid ${isToday ? 'var(--volt)' : 'var(--line-07)'}`,
                borderRadius: 'var(--r-md)',
                padding: '10px 12px',
              }}
            >
              <div className="kick" style={{ color: isToday ? 'var(--volt)' : 'var(--ink-3)' }}>{p.d}</div>
              <div style={{ fontSize: 13, fontWeight: 600, marginTop: 3, lineHeight: 1.25 }}>{p.name}</div>
            </button>
          )
        })}
      </div>

      {/* Goals footer */}
      <div className="tip muted" style={{ marginTop: 14, textAlign: 'center' }}>{goalsLine}</div>
    </div>
  )
}
